import React, { useState, useEffect } from 'react';
import {
  Cpu, Save, TestTube2, CheckCircle, AlertCircle, Loader2,
  Eye, EyeOff, RefreshCw, ChevronDown,
} from 'lucide-react';
import * as llmSvc from '../services/llmProviderService';
import type { LLMProviderConfig } from '../services/llmProviderService';

const AWS_REGIONS = [
  'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
  'eu-west-1', 'eu-west-2', 'eu-central-1',
  'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1',
  'ca-central-1', 'sa-east-1',
];

const LLMProviderPage: React.FC = () => {
  const [config, setConfig] = useState<LLMProviderConfig | null>(null);
  const [loading, setLoading] = useState(true);

  // Form state
  const [provider, setProvider] = useState<'ollama' | 'bedrock'>('ollama');
  const [ollamaUrl, setOllamaUrl] = useState('http://localhost:11435');
  const [awsAccessKey, setAwsAccessKey] = useState('');
  const [awsSecretKey, setAwsSecretKey] = useState('');
  const [awsRegion, setAwsRegion] = useState('us-east-1');
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'ok' | 'err'>('idle');

  // Test
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);

  // Models
  const [models, setModels] = useState<llmSvc.ModelInfo[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    loadModels();
  }, [provider]);

  const loadConfig = async () => {
    try {
      const cfg = await llmSvc.getProviderConfig();
      setConfig(cfg);
      setProvider(cfg.provider);
      setOllamaUrl(cfg.ollama_base_url);
      setAwsRegion(cfg.aws_region);
    } catch (err) {
      console.error('Failed to load LLM provider config', err);
    } finally {
      setLoading(false);
    }
  };

  const loadModels = async () => {
    setModelsLoading(true);
    try {
      const res = await llmSvc.getProviderModels(provider);
      setModels(res.models);
    } catch {
      setModels([]);
    } finally {
      setModelsLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus('idle');
    try {
      await llmSvc.updateProviderConfig({
        provider,
        ollama_base_url: ollamaUrl,
        aws_access_key_id: awsAccessKey || undefined,
        aws_secret_access_key: awsSecretKey || undefined,
        aws_region: awsRegion,
      });
      setSaveStatus('ok');
      await loadConfig();
      // Clear credential fields after save (they're now stored encrypted)
      setAwsAccessKey('');
      setAwsSecretKey('');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err) {
      setSaveStatus('err');
      setTimeout(() => setSaveStatus('idle'), 4000);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await llmSvc.testProviderConnection(provider);
      setTestResult({ ok: true, msg: `Connected to ${result.model} — ${result.response}` });
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Connection failed';
      setTestResult({ ok: false, msg });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading provider config...
      </div>
    );
  }

  const bedrockModelsByVendor = models.reduce<Record<string, llmSvc.ModelInfo[]>>((acc, m) => {
    const vendor = m.provider || 'Other';
    if (!acc[vendor]) acc[vendor] = [];
    acc[vendor].push(m);
    return acc;
  }, {});

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold text-white mb-2">LLM Provider</h1>
        <p className="text-slate-400">
          Configure the AI model runtime for all trading agents. Choose between a local Ollama
          instance or AWS Bedrock for cloud-hosted models.
        </p>
      </div>

      {/* Provider selector */}
      <div className="bg-slate-800/60 backdrop-blur-md rounded-xl border border-slate-700 p-6 space-y-6">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Cpu className="w-5 h-5 text-blue-400" />
          Provider
        </h2>

        <div className="flex gap-4">
          {([
            {
              id: 'ollama' as const,
              label: 'Ollama',
              desc: 'Local LLM server — open-source models on your hardware',
              badge: 'Free / Local',
              badgeClass: 'bg-green-400/10 text-green-300',
            },
            {
              id: 'bedrock' as const,
              label: 'AWS Bedrock',
              desc: 'Fully-managed cloud inference — Claude, Llama, Mistral & more',
              badge: 'Cloud / Paid',
              badgeClass: 'bg-orange-400/10 text-orange-300',
            },
          ] as const).map(p => (
            <button
              key={p.id}
              onClick={() => setProvider(p.id)}
              className={`flex-1 p-4 rounded-xl border-2 text-left transition ${
                provider === p.id
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-slate-600 hover:border-slate-500 bg-slate-900/40'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-white">{p.label}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.badgeClass}`}>
                  {p.badge}
                </span>
              </div>
              <p className="text-xs text-slate-400">{p.desc}</p>
            </button>
          ))}
        </div>

        {/* Ollama config */}
        {provider === 'ollama' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Ollama Base URL</label>
              <input
                value={ollamaUrl}
                onChange={e => setOllamaUrl(e.target.value)}
                placeholder="http://localhost:11435"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
              />
              <p className="text-xs text-slate-500 mt-1">
                The URL where your Ollama server is running. Default port is 11434 or 11435.
              </p>
            </div>
          </div>
        )}

        {/* Bedrock config */}
        {provider === 'bedrock' && (
          <div className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-300">
              Credentials are stored encrypted using Fernet encryption. They are never returned in API responses.
              Leave credential fields blank to use IAM roles or environment variables (AWS_ACCESS_KEY_ID etc).
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  AWS Access Key ID
                  {config?.has_aws_credentials && (
                    <span className="ml-2 text-xs text-green-400">● stored</span>
                  )}
                </label>
                <input
                  value={awsAccessKey}
                  onChange={e => setAwsAccessKey(e.target.value)}
                  placeholder={config?.has_aws_credentials ? '(leave blank to keep existing)' : 'AKIAIOSFODNN7EXAMPLE'}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500 font-mono"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">AWS Secret Access Key</label>
                <div className="relative">
                  <input
                    type={showSecret ? 'text' : 'password'}
                    value={awsSecretKey}
                    onChange={e => setAwsSecretKey(e.target.value)}
                    placeholder={config?.has_aws_credentials ? '(leave blank to keep existing)' : 'wJalrXUtnFEMI/K7MDENG'}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 pr-10 text-white text-sm focus:outline-none focus:border-blue-500 font-mono"
                  />
                  <button
                    onClick={() => setShowSecret(v => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    type="button"
                  >
                    {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">AWS Region</label>
              <select
                value={awsRegion}
                onChange={e => setAwsRegion(e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
              >
                {AWS_REGIONS.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Choose the region where you have Bedrock model access enabled in the AWS console.
              </p>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-3 pt-2 border-t border-slate-700">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium disabled:opacity-50 transition"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Configuration
          </button>
          <button
            onClick={handleTest}
            disabled={testing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm transition disabled:opacity-50"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube2 className="w-4 h-4" />}
            Test Connection
          </button>
          {saveStatus === 'ok' && (
            <span className="flex items-center gap-1.5 text-green-400 text-sm">
              <CheckCircle className="w-4 h-4" /> Saved
            </span>
          )}
          {saveStatus === 'err' && (
            <span className="flex items-center gap-1.5 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" /> Save failed
            </span>
          )}
        </div>

        {/* Test result */}
        {testResult && (
          <div className={`flex items-start gap-2 p-3 rounded-lg text-sm border ${
            testResult.ok
              ? 'bg-green-500/10 border-green-500/20 text-green-300'
              : 'bg-red-500/10 border-red-500/20 text-red-300'
          }`}>
            {testResult.ok
              ? <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              : <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            }
            <span className="font-mono text-xs leading-relaxed">{testResult.msg}</span>
          </div>
        )}
      </div>

      {/* Available models */}
      <div className="bg-slate-800/60 backdrop-blur-md rounded-xl border border-slate-700 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">
            {provider === 'bedrock' ? 'Available Bedrock Models' : 'Available Ollama Models'}
          </h2>
          <button
            onClick={loadModels}
            disabled={modelsLoading}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${modelsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {modelsLoading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading models...
          </div>
        ) : models.length === 0 ? (
          <p className="text-slate-500 text-sm py-4">No models available. Check your connection settings.</p>
        ) : provider === 'bedrock' ? (
          /* Bedrock: grouped by vendor */
          <div className="space-y-4">
            {Object.entries(bedrockModelsByVendor).map(([vendor, vendorModels]) => (
              <div key={vendor}>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{vendor}</p>
                <div className="grid gap-1.5">
                  {vendorModels.map(m => (
                    <div key={m.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/50">
                      <span className="text-white text-sm font-medium">{m.name}</span>
                      <span className="text-slate-500 text-xs font-mono ml-auto">{m.id}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <p className="text-xs text-slate-500 mt-2">
              Ensure these models are enabled in your AWS Bedrock console for the selected region.
            </p>
          </div>
        ) : (
          /* Ollama: flat list */
          <div className="grid gap-1.5">
            {models.map(m => (
              <div key={m.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/50">
                <span className="text-white text-sm font-medium">{m.name || m.id}</span>
                <span className="text-slate-500 text-xs font-mono ml-auto">{m.id}</span>
              </div>
            ))}
            {provider === 'ollama' && (
              <p className="text-xs text-slate-500 mt-1">
                Models are fetched live from your Ollama instance. Pull new models with <code className="bg-slate-700 px-1 rounded">ollama pull &lt;model&gt;</code>.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Current status */}
      {config && (
        <div className="bg-slate-800/40 rounded-xl border border-slate-700/50 p-4 text-xs text-slate-400 space-y-1">
          <p>Active provider: <span className="text-white font-medium capitalize">{config.provider}</span></p>
          {config.provider === 'ollama' && (
            <p>Ollama URL: <span className="text-white font-mono">{config.ollama_base_url}</span></p>
          )}
          {config.provider === 'bedrock' && (
            <>
              <p>Region: <span className="text-white font-mono">{config.aws_region}</span></p>
              <p>Credentials: <span className={config.has_aws_credentials ? 'text-green-400' : 'text-amber-400'}>
                {config.has_aws_credentials ? 'Stored (encrypted)' : 'Not set — using IAM/env vars'}
              </span></p>
            </>
          )}
          {config.updated_at && (
            <p>Last updated: <span className="text-slate-300">{new Date(config.updated_at).toLocaleString()}</span></p>
          )}
        </div>
      )}
    </div>
  );
};

export default LLMProviderPage;
